import type React from 'react';

import aiHelperImg from '~/static/assets/images/landing-page/ai_helper.png';
import integrationsImg from '~/static/assets/images/landing-page/integrations_photo.png';
import phoneSmsImg from '~/static/assets/images/landing-page/phone_sms_photo.png';
import { getOptimizedImageSources } from '../../utils/imageOptimization';
import { LazyImage } from '../LazyImage';

interface BusinessUseCase {
  id: string;
  title: string;
  description: string;
  imagePath: ImageAsset;
  learnMoreUrl: string;
  category: string;
}

interface BusinessUseCasesSectionProps {
  useCases?: BusinessUseCase[];
  sectionTitle?: string;
  sectionSubtitle?: string;
}

const defaultUseCases: BusinessUseCase[] = [
  {
    id: 'education',
    title: 'Education and learn teacher',
    description: 'AI HELPER',
    imagePath: aiHelperImg,
    learnMoreUrl: '#education',
    category: 'Education',
  },
  {
    id: 'teams-integration',
    title: 'Sangoma for Microsoft Team',
    description: 'INTEGRATIONS',
    imagePath: integrationsImg,
    learnMoreUrl: '#teams-integration',
    category: 'Integration',
  },
  {
    id: 'retail-ecommerce',
    title: 'Retail and e-commerce solutions',
    description: 'PHONE AND SMS',
    imagePath: phoneSmsImg,
    learnMoreUrl: '#retail-ecommerce',
    category: 'Retail',
  },
];

export const BusinessUseCasesSection: React.FC<BusinessUseCasesSectionProps> = ({
  useCases = defaultUseCases,
  sectionTitle = '',
  sectionSubtitle = '',
}) => {
  const handleLearnMoreClick = (useCase: BusinessUseCase) => {
    // Track learn more click for analytics
    console.log(`Learn more clicked for ${useCase.category}: ${useCase.title}`);

    // Navigate to learn more URL
    if (useCase.learnMoreUrl !== '#') {
      window.open(useCase.learnMoreUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderUseCaseCard = (useCase: BusinessUseCase) => {
    const optimizedSources = getOptimizedImageSources(useCase.imagePath.src);

    return (
      <article key={useCase.id} className="business-use-case-card">
        <div className="use-case-image-container">
          <LazyImage
            src={useCase.imagePath.src}
            webpSrc={optimizedSources.webp}
            fallbackSrc={optimizedSources.fallback}
            placeholder={optimizedSources.placeholder}
            width={useCase.imagePath.width}
            height={useCase.imagePath.height}
            alt={`${useCase.title} - ${useCase.description} use case demonstration`}
            className="use-case-image"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="use-case-content">
          <p className="use-case-category">{useCase.description}</p>
          <h3 className="use-case-title" id={`use-case-title-${useCase.id}`}>
            {useCase.title}
          </h3>
          <button
            type="button"
            className="use-case-learn-more"
            onClick={() => handleLearnMoreClick(useCase)}
            aria-labelledby={`use-case-title-${useCase.id}`}
            aria-describedby={`use-case-desc-${useCase.id}`}
          >
            Learn more
          </button>
          <div id={`use-case-desc-${useCase.id}`} className="sr-only">
            Learn more about {useCase.title} solutions and how they can benefit your business
          </div>
        </div>
      </article>
    );
  };

  return (
    <section className="business-use-cases-section" aria-labelledby="business-section-title">
      <div className="business-use-cases-container">
        <div className="business-use-cases-content">
          <header className="business-section-header">
            <h2 id="business-section-title" className="business-section-title">
              {sectionTitle}
              <button
                type="button"
                className="section-nav-arrow"
                aria-label="Navigate to next section"
                aria-describedby="nav-arrow-desc"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </h2>
            <div id="nav-arrow-desc" className="sr-only">
              Scroll to the next section of the page
            </div>
            {sectionSubtitle && <p className="business-section-subtitle">{sectionSubtitle}</p>}
          </header>

          <ul className="use-cases-grid" aria-label="Business use cases">
            {useCases.map((useCase) => (
              <li key={useCase.id}>{renderUseCaseCard(useCase)}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
